import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { PremiumFooter } from '@/components/premium/PremiumFooter';
import { ProductCard } from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { SEO, ItemListStructuredData, BreadcrumbStructuredData, FAQStructuredData } from '@/components/SEO';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { RelatedPages } from '@/components/RelatedPages';
import { CinematicCategoryHero } from '@/components/CinematicCategoryHero';
import { Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';
import { getCategoryVideo, NOISE_CHECK_AUDIO } from '@/config/mediaConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Noise Check Component for Privacy Booths with Audio
const NoiseCheckSection = () => {
  const [isInside, setIsInside] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);

  const startAudio = (insideMode = isInside) => {
    try {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      const audioUrl = insideMode ? NOISE_CHECK_AUDIO.inside : NOISE_CHECK_AUDIO.outside;
      const audio = new Audio(audioUrl);
      audio.loop = true;
      audio.volume = 0.7;
      audio.play().catch(e => console.log('Audio play failed:', e));
      setAudioElement(audio);
      setIsPlaying(true);
    } catch (e) {
      console.error('Audio error:', e);
    }
  };

  const stopAudio = () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
    setAudioElement(null);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (isPlaying) stopAudio();
    else startAudio();
  };

  const handleToggle = () => {
    const newIsInside = !isInside;
    setIsInside(newIsInside);
    if (isPlaying) startAudio(newIsInside);
  };

  useEffect(() => {
    return () => {
      if (audioElement) audioElement.pause();
    };
  }, [audioElement]);
  
  return (
    <div className="bg-[#f8f7f4] border border-gray-200 rounded-2xl p-8 mb-12 overflow-hidden relative">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(0,0,0,0.03) 50px, rgba(0,0,0,0.03) 51px)`
        }} />
      </div>
      <div className="relative z-10">
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-2">
            'Check the <span className="text-primary">Miracle of Noise Reduction</span>'
          </h3>
          <p className="text-gray-600 text-lg">Experience the difference our soundproof booths make</p>
        </div>
        <div className="flex justify-center mb-6">
          <button
            onClick={togglePlay}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold uppercase tracking-wider transition-all ${
              isPlaying ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-primary hover:bg-primary/90 text-white'
            }`}
          >
            {isPlaying ? <><VolumeX className="w-5 h-5" /> Stop Audio Demo</> : <><Volume2 className="w-5 h-5" /> Play Audio Demo</>}
          </button>
        </div>
        <div className="flex items-center justify-center gap-6 mb-8">
          <div className={`flex flex-col items-center transition-all duration-500 ${!isInside ? 'scale-110' : 'opacity-50'}`}>
            <Volume2 className={`w-10 h-10 ${!isInside ? 'text-red-500' : 'text-gray-500'}`} />
            <span className="text-xs mt-1 text-gray-400">Noisy</span>
          </div>
          <div className="flex-1 max-w-lg h-24 bg-gray-100 rounded-xl p-4 flex items-center justify-center gap-1 overflow-hidden">
            {[...Array(40)].map((_, i) => {
              const baseHeight = Math.sin(i * 0.3) * 30 + 35;
              const outsideHeight = baseHeight + Math.random() * 20;
              const insideHeight = Math.max(8, baseHeight * 0.2);
              return (
                <div key={i} className={`w-1.5 rounded-full transition-all duration-500 ${isInside ? 'bg-gradient-to-t from-primary to-green-400' : 'bg-gradient-to-t from-red-600 to-orange-400'}`}
                  style={{ height: `${isInside ? insideHeight : outsideHeight}%`, animationDelay: `${i * 50}ms`, animation: isPlaying && !isInside ? 'pulse 0.5s ease-in-out infinite alternate' : 'none' }}
                />
              );
            })}
          </div>
          <div className={`flex flex-col items-center transition-all duration-500 ${isInside ? 'scale-110' : 'opacity-50'}`}>
            <VolumeX className={`w-10 h-10 ${isInside ? 'text-primary' : 'text-gray-500'}`} />
            <span className="text-xs mt-1 text-gray-400">Quiet</span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-4">
          <span className={`text-lg font-bold uppercase tracking-wider transition-colors ${!isInside ? 'text-red-500' : 'text-gray-500'}`}>OUTSIDE</span>
          <button onClick={handleToggle} className={`relative w-20 h-10 rounded-full transition-colors duration-300 ${isInside ? 'bg-primary' : 'bg-red-500'}`}>
            <div className={`absolute top-1 w-8 h-8 bg-white rounded-full shadow-lg transition-transform duration-300 ${isInside ? 'translate-x-10' : 'translate-x-1'}`} />
          </button>
          <span className={`text-lg font-bold uppercase tracking-wider transition-colors ${isInside ? 'text-primary' : 'text-gray-500'}`}>INSIDE</span>
        </div>
        <div className="mt-6 text-center">
          <p className={`text-sm font-medium transition-all duration-500 ${isInside ? 'text-primary' : 'text-red-400'}`}>
            {isInside ? 'Noise Reduction: Up to 35dB - Perfect for calls & focus work' : 'Typical Office Noise: 65-75dB - Distractions everywhere'}
          </p>
          {isPlaying && <p className="text-xs text-gray-500 mt-2">Audio playing - Toggle switch to hear the difference!</p>}
        </div>
      </div>
      <style>{`@keyframes pulse { 0% { transform: scaleY(0.8); } 100% { transform: scaleY(1.2); } }`}</style>
    </div>
  );
};

const subcategoriesMap = {
  'Modular Office Workstation': ['Cluster of 2', 'Cluster of 4', 'Cluster of 4 Large'],
  'Storage Cabinets': ['Two Drawer Pedestal', 'Three Drawer Pedestal'],
  'Privacy Booths': ['Phone Booth', 'Meeting Booth', 'Meeting Booth Large']
};

const categories = [
  'Modular Office Workstation',
  'Storage Cabinets',
  'Privacy Booths'
];

const CATEGORY_FAQS = {
  'Modular Office Workstation': [
    { question: 'What is the starting price for modular office workstations?', answer: 'Modular office workstations at MFS start from ₹5,320 for a basic 2-seater linear configuration. Prices vary based on material grade, cluster size, and accessories.' },
    { question: 'How long does workstation installation take?', answer: 'Using our Kholo-Jodo-Lagalo tool-free system, a typical workstation cluster can be assembled in 2-4 hours. Free professional installation is included in Mumbai & Navi Mumbai.' },
    { question: 'Can I add more workstations later?', answer: 'Yes, our modular system is designed for scalability. You can add modules seamlessly — go from a cluster of 2 to a cluster of 6 without replacing existing units.' },
  ],
  'Privacy Booths': [
    { question: 'How soundproof are MFS privacy booths?', answer: 'Our booths provide 30-40 dB noise reduction, which effectively blocks typical office noise. They feature acoustic panels, sealed doors, and ventilation designed for quiet operation.' },
    { question: 'Do privacy booths need any construction or electrical work?', answer: 'No. MFS booths are fully self-contained with built-in LED lighting, ventilation, USB charging, and power outlets. They arrive pre-assembled and can be placed anywhere with a standard power socket.' },
    { question: 'What sizes of privacy booths are available?', answer: 'We offer 1-person phone pods, 2-person meeting pods, 4-person conference booths, and 6-8 person large meeting pods. All are available for same-day setup.' },
  ],
  'Storage Cabinets': [
    { question: 'What types of storage cabinets does MFS offer?', answer: 'We offer sliding door cabinets, filing cabinets, under-desk pedestals, credenzas, and full-height storage units. All come with premium laminate finish and lock systems.' },
    { question: 'Are storage cabinets included with workstation orders?', answer: 'Under-desk pedestals can be bundled with workstation orders at discounted rates. Contact us for combo pricing on bulk orders that include both workstations and storage.' },
    { question: 'Do the cabinets come with locks?', answer: 'Yes, all our storage cabinets come with key-lock systems. We also offer combination lock upgrades for high-security applications.' },
  ],
};

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || '');

  // When URL changes, update state. Auto-select first subcategory if none specified.
  useEffect(() => {
    const catFromUrl = searchParams.get('category') || '';
    const subFromUrl = searchParams.get('subcategory') || '';
    
    setSelectedCategory(catFromUrl);

    if (catFromUrl && !subFromUrl && subcategoriesMap[catFromUrl]) {
      // Auto-select first subcategory
      const firstSub = subcategoriesMap[catFromUrl][0];
      setSelectedSubcategory(firstSub);
      setSearchParams({ category: catFromUrl, subcategory: firstSub }, { replace: true });
    } else {
      setSelectedSubcategory(subFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    // Don't fetch if category has subcategories but none is selected yet (auto-select is pending)
    if (selectedCategory && subcategoriesMap[selectedCategory] && !selectedSubcategory) return;
    fetchProducts();
  }, [selectedCategory, selectedSubcategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedSubcategory) params.subcategory = selectedSubcategory;
      const response = await axios.get(`${API_URL}/products`, { params });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    if (category && subcategoriesMap[category]) {
      const firstSub = subcategoriesMap[category][0];
      setSelectedCategory(category);
      setSelectedSubcategory(firstSub);
      setSearchParams({ category, subcategory: firstSub });
    }
  };

  const handleSubcategoryChange = (subcat) => {
    setSelectedSubcategory(subcat);
    setSearchParams({ category: selectedCategory, subcategory: subcat });
  };

  const categoryVideo = getCategoryVideo(selectedCategory);

  const getCategoryReview = () => {
    if (selectedCategory === 'Modular Office Workstation') {
      return {
        text: "These workstations transformed our office! The build quality is exceptional and the modular design allows for easy reconfiguration.",
        author: "Rajesh Sharma",
        role: "IT Company Director",
        company: "TechVision Solutions",
        features: [
          "Action Tesa / Merino Prelaminated particle board",
          "High-quality fittings – Joineries & Studs (Ezeefit)",
          "Precision Engineering through Machine-Driven Manufacturing",
          "Environment-friendly Furniture"
        ]
      };
    } else if (selectedCategory === 'Storage Cabinets') {
      return {
        text: "Perfect storage solution for our office. The drawers glide smoothly and the finish matches beautifully.",
        author: "Priya Patel",
        role: "Office Manager",
        company: "Greenfield Enterprises",
        features: [
          "Heavy-duty steel construction",
          "Smooth ball-bearing drawer slides",
          "Central locking system for security",
          "Powder-coated scratch-resistant finish"
        ]
      };
    } else if (selectedCategory === 'Privacy Booths') {
      return {
        text: "The privacy booths are a game-changer! Crystal clear audio for video calls, excellent soundproofing.",
        author: "Amit Verma",
        role: "HR Head",
        company: "Global Tech Hub",
        features: [
          "Superior acoustic soundproofing panels",
          "Integrated LED lighting system",
          "Silent ventilation with air circulation",
          "Built-in power outlets & USB ports"
        ]
      };
    }
    return {
      text: "Outstanding quality furniture that transformed our entire office space.",
      author: "Sanjay Mehta",
      role: "Managing Director",
      company: "Innovate Corp",
      features: [
        "Premium quality materials",
        "Modular & customizable designs",
        "Easy assembly - Kholo, Jodo, Lagalo!",
        "5-year warranty on all products"
      ]
    };
  };

  const categoryReview = getCategoryReview();
  const currentSubcategories = selectedCategory ? (subcategoriesMap[selectedCategory] || []) : [];

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/products' },
    ...(selectedCategory ? [{ name: selectedCategory }] : [])
  ];

  // If no category selected, default to first category
  useEffect(() => {
    if (!selectedCategory) {
      handleCategoryChange(categories[0]);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={selectedCategory 
          ? `${selectedCategory === 'Modular Office Workstation' ? 'Modular Workstations' : selectedCategory} | Buy Online | MFS`
          : 'Office Furniture | Workstations, Pods & Storage | MFS'}
        description={selectedCategory === 'Modular Office Workstation' 
          ? 'Shop modular workstations online. Best workstation desk price. Free shipping in Mumbai.'
          : selectedCategory === 'Privacy Booths'
          ? 'Buy privacy booth, office phone booths, focus pods & soundproof booths online.'
          : selectedCategory === 'Storage Cabinets'
          ? 'Buy filing cabinets, office credenzas, under desk storage online.'
          : 'Order office pods, modular workstations, phone booths, storage cabinets online.'}
        keywords="modular workstations, office phone booths, privacy booth, storage cabinets"
        canonical="/products"
      />
      <BreadcrumbStructuredData items={breadcrumbItems} />
      <ItemListStructuredData products={products} listName={selectedCategory || 'All Office Furniture'} />
      {selectedCategory && CATEGORY_FAQS[selectedCategory] && (
        <FAQStructuredData faqs={CATEGORY_FAQS[selectedCategory]} />
      )}
      <PremiumHeader />

      {/* Cinematic Hero */}
      {selectedCategory && (
        <CinematicCategoryHero category={selectedCategory} />
      )}

      {/* Noise Check - Privacy Booths only */}
      {selectedCategory === 'Privacy Booths' && (
        <div className="container mx-auto px-4 sm:px-8 pt-8">
          <NoiseCheckSection />
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-8 py-12" id="products">

        {/* Visual Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />

        {/* Category Tabs — No ALL button */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3" data-testid="category-filters">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => handleCategoryChange(category)}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className="rounded-none uppercase tracking-wider font-bold"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Subcategory Dropdown — compact, inside selected category */}
        {selectedCategory && currentSubcategories.length > 0 && (
          <div className="mb-8" data-testid="subcategory-filters">
            <select
              value={selectedSubcategory}
              onChange={(e) => handleSubcategoryChange(e.target.value)}
              className="appearance-none bg-white border-2 border-[#1a1a1a] text-[#1a1a1a] px-5 py-2.5 pr-10 text-xs font-bold uppercase tracking-wider cursor-pointer focus:outline-none focus:border-[#c5a059] transition-colors"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%231a1a1a' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
              data-testid="subcategory-dropdown"
            >
              {currentSubcategories.map((subcat) => (
                <option key={subcat} value={subcat}>{subcat.toUpperCase()}</option>
              ))}
            </select>
          </div>
        )}

        {/* Review & Features Section */}
        <div className="mb-8 bg-gradient-to-r from-[#c5a059]/10 via-white to-[#c5a059]/10 border border-[#c5a059]/30 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Tagline */}
            <div className="flex flex-col justify-center bg-[#F8F6F1] p-4 rounded-lg">
              <p className="text-3xl font-black text-[#c5a059] tracking-tight leading-tight">
                KHOLO<span className="text-[#1a1a1a]">.</span> JODO<span className="text-[#1a1a1a]">.</span> LAGALO<span className="text-[#1a1a1a]">.</span>
              </p>
              <p className="text-sm text-gray-600 mt-2 uppercase tracking-wider font-semibold">Open. Assemble. Install.</p>
            </div>

            {/* Review */}
            <div>
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 fill-[#c5a059]" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed italic mb-2">"{categoryReview.text}"</p>
              <p className="text-sm font-bold text-[#1a1a1a]">{categoryReview.author}</p>
              <p className="text-xs text-gray-500">{categoryReview.role}, {categoryReview.company}</p>
            </div>

            {/* Features */}
            <div>
              <p className="text-sm font-black uppercase tracking-wider text-[#c5a059] mb-3">Features & Quality</p>
              <ul className="space-y-1.5">
                {categoryReview.features.map((feature, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-[#c5a059] font-bold">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Special Offer */}
            <div className="bg-[#F8F6F1] p-5 border border-[#c5a059]/30 rounded-lg flex flex-col justify-center">
              <p className="text-sm text-gray-700 leading-relaxed">
                <span className="font-black text-[#c5a059] text-base">MFS</span> would like to help in expanding your team! We offer an additional <span className="font-black text-[#c5a059] text-lg">2% OFF</span> on your first buy with us.
              </p>
              <p className="text-sm font-black text-[#c5a059] mt-3">
                Go ahead, fill your cart. Let's join hands!
              </p>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p className="text-xl uppercase tracking-widest text-gray-400">LOADING...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl uppercase tracking-widest text-gray-400">NO PRODUCTS FOUND</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} featured={false} />
            ))}
          </div>
        )}

        {/* Category FAQ Section */}
        {selectedCategory && CATEGORY_FAQS[selectedCategory] && (
          <section className="mt-16 border-t border-gray-100 pt-10">
            <h2 className="text-lg font-extrabold text-[#1a1a1a] mb-6 uppercase tracking-tight">
              Frequently Asked Questions — {selectedCategory}
            </h2>
            <div className="space-y-3 max-w-3xl">
              {CATEGORY_FAQS[selectedCategory].map((faq, i) => (
                <details key={i} className="group border border-gray-100 open:border-[#c5a059] transition-colors">
                  <summary className="flex justify-between items-center cursor-pointer px-5 py-3 text-sm font-semibold text-[#1a1a1a] hover:text-[#c5a059]">
                    {faq.question}
                    <span className="text-[#c5a059] group-open:rotate-45 transition-transform text-lg ml-2">+</span>
                  </summary>
                  <p className="px-5 pb-3 text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Interlinks */}
        <RelatedPages pageKey="products" className="mt-12" />
      </div>
      <PremiumFooter />
    </div>
  );
}
